/*
###################################################################
Fitinsur Open Insurance - Copyright 2019-2023 - All Rights Reserved
###################################################################
*/

import React, { useContext, useEffect, useState } from "react";
import {
  NotificationsContainer,
  NotificationsCounter,
  NotificationsHeader,
  NotificationsSettings,
  NotificationsBody,
  Scrollbox,
  Notification,
  NotificationTitle,
  NotificationContent,
  NotificationSubtitle,
  NotificationText,
  NotificationTextGrouping,
  Text,
  TooltipCard,
  TooltipText,
  TooltipBox,
} from "./styles";
import Icon from "components/Icon/index";
import ModalConfirmation from "components/ModalConfirmation/index";
import moment from "moment";
import SettingsNotifications from "./SettingsNotifications";
import { ApiNotification, MessageCenter } from 'utils';
import { GlobalAuthContext, GlobalLoaderContext } from 'store/context';
import { useHistory } from "react-router-dom";
import CacheManager from "utils/src/cache-manager";

export default function NotificationsProfile({ changeDefaultState, showModal }) {
  const cache = new CacheManager();
  const history = useHistory();
  const mc = new MessageCenter();
  const authContext = useContext(GlobalAuthContext);
  const apiNotification = new ApiNotification();
  const [confirmationModal, setConfirmationModal] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notificationGroups, setNotificationGroups] = useState([]);
  const [unreadCounter, setUnreadCounter] = useState(0);

  const [tooltips, setTooltips] = useState([]);
  const region = authContext.region === 'en-GB' ? 'en' : 'en';

  const getNotifications = async () => {
    try {
      apiNotification.setAuthToken(authContext.token);
      const response = await apiNotification
        .getNotifications(1)
        .then((response) => response.json());

      let notifications = response.ret_value;

      if (response.ret_code !== 200) return;

      let newNotifications = { title: '', notifications: [] };
      let recentNotifications = { title: 'Last 7 days', notifications: [] };
      let olderNotification = { title: 'Older', notifications: [] };

      notifications.map(notification => {
        const notificationDate = moment(notification.createdAt);
        const currentDate = moment(new Date());
        const diff = currentDate.diff(notificationDate, 'days');

        if (diff > 7) {
          olderNotification.notifications.push(notification);
        } else if (diff === 0) {
          newNotifications.notifications.push(notification)
        } else {
          recentNotifications.notifications.push(notification)
        };
      })

      setUnreadCounter(notifications.filter(v => !v.isRead).length)

      setNotificationGroups([
        newNotifications,
        recentNotifications,
        olderNotification
      ])

    } catch (err) {
      console.log("getNotifications err", err)
    }
  }

  useEffect(() => {
    getNotifications()

    cache.getTooltip("DOMAINS", region, "357", [591], authContext.token).then(msg => {
      let obj = [];
      msg.map(element => {
        obj[element.MODEL_ITEM_ID] = element.MSG_TEXT;
      })
      setTooltips(obj)
    }).catch(err => console.log("error", err));
  }, [])

  const handleReadMessage = async (groupIndex, currentNotification) => {
    try {
      apiNotification.setAuthToken(authContext.token);
      const response = await apiNotification.markAsDone([currentNotification.idNotification], false).then((response) => response.json());
      if (response.ret_code !== 200) return;

      setNotificationGroups(notificationGroups.map((group, index) => {
        if (index !== groupIndex) return group;

        group.notifications = group.notifications.filter((notification) => notification.idNotification !== currentNotification.idNotification);
        return group;
      }));

      mc.successWithAction(
        () => revertReadMessage({ groupIndex, currentNotification }),
        "Notification has marked as done"
      );
    } catch (err) {
      console.log("err", err)
    }
  };

  const revertReadMessage = async ({ groupIndex, currentNotification }) => {
    try {
      apiNotification.setAuthToken(authContext.token);
      const response = await apiNotification.markAsDone([currentNotification.idNotification], true).then((response) => response.json());
      if (response.ret_code !== 200) return;

      setNotificationGroups(notificationGroups.map((group, index) => {
        if (index !== groupIndex) return group;

        group.notifications = [...group.notifications, currentNotification].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        return group;
      }));
    } catch (err) {
      console.log("err", err)
    }
  };

  const handleMarkAllAsDone = async () => {
    try {
      let allNotifications = [];
      notificationGroups.map((group) => {
        allNotifications = [...allNotifications, ...group.notifications]
      });

      apiNotification.setAuthToken(authContext.token);
      const response = await apiNotification.markAsDone(allNotifications.map(notification => notification.idNotification)).then((response) => response.json());

      setNotificationGroups([]);
    } catch (err) {
      console.log("err", err)
    }
  };


  return (
    <>
      {!showModal ?
        <NotificationsContainer>
          <NotificationsHeader>
            <NotificationsCounter>
              <Icon name="bell-notifications" />
              <Text>Notifications ({unreadCounter || 0})</Text>
            </NotificationsCounter>
            <NotificationsSettings>
              <TooltipCard>
                <TooltipText>
                  <Icon
                    name="edit-settings"
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      changeDefaultState(!showModal)
                    }}
                  />
                </TooltipText>
                <TooltipBox>
                  {tooltips[591]}
                </TooltipBox>
              </TooltipCard>
              <Icon
                name="check-ok-green"
                title="Mark all as done"
                onClick={() => setConfirmationModal(!confirmationModal)}
              />
            </NotificationsSettings>
          </NotificationsHeader>
          <Scrollbox onScroll={e => e.stopPropagation()}>
            <NotificationsBody>
              {notificationGroups.map((group, groupIndex) => (
                <>
                  {(group.title && group.notifications?.length) ? <NotificationTextGrouping>{group.title}</NotificationTextGrouping> : <></>}
                  {group.notifications.map(notification => (
                    <Notification
                      className={notification.isRead ? "not-read" : "read"}
                      onClick={() => {
                        history.push(`/notification-proxy/${notification.idNotification}/${notification.idOperation}`)
                      }}
                    >
                      <NotificationContent>
                        <NotificationTitle>
                          <NotificationText style={{ paddingBottom: '13px' }}>
                            {notification.subject}
                          </NotificationText>
                          <Icon
                            name="check-ok"
                            title="Mark as done"
                            id="icon-checked"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleReadMessage(groupIndex, notification);
                            }}
                          />
                        </NotificationTitle>
                        <NotificationSubtitle>
                          <NotificationText>
                            {notification.message}
                          </NotificationText>
                          <NotificationText style={{ fontWeight: 'normal' }}>
                            {moment(notification.createdAt).fromNow()}
                          </NotificationText>
                        </NotificationSubtitle>
                      </NotificationContent>
                    </Notification>
                  ))}
                </>
              ))}
            </NotificationsBody>
            <ModalConfirmation
              text="Are you sure you want to mark all as done?"
              show={confirmationModal}
              onConfirm={() => {
                setConfirmationModal(false);
                handleMarkAllAsDone();
              }}
              onCancel={() => {
                setConfirmationModal(false);
              }}
            />
          </Scrollbox>
        </NotificationsContainer> : <SettingsNotifications changeState={changeDefaultState} showModal={showModal} />}
    </>
  );
}
